import { flushSync } from 'react-dom';

export const createGlobalKeyHandler = ({
                                           rowVirtualizer,
                                           stocks,
                                           selectedRowIndexRef,
                                           updateSelectedRowIndex
                                       }) => {
    const pending = { current: null };
    let rafId = null;

    const doArrowMove = () => {
        const dir = pending.current;
        pending.current = null;
        if (!dir) return;

        const cur = selectedRowIndexRef.current;
        if (cur == null) return;

        // compute next index
        const next = dir === 'down'
            ? Math.min(cur + 1, stocks.length - 1)
            : Math.max(cur - 1, 0);

        // determine which rows are truly visible (excluding overscan)
        const scrollEl     = rowVirtualizer.getScrollElement?.() || rowVirtualizer.scrollElement;
        const scrollTop    = scrollEl.scrollTop;
        const viewportH    = scrollEl.clientHeight;
        const virtItems    = rowVirtualizer.getVirtualItems();
        const visibleItems = virtItems.filter(item =>
            item.start >= scrollTop &&
            item.start + item.size <= scrollTop + viewportH
        );

        const firstVisible = visibleItems[0]?.index ?? virtItems[0]?.index;
        const lastVisible  = visibleItems[visibleItems.length - 1]?.index ?? virtItems[virtItems.length - 1]?.index;

        // only scroll if next falls outside the true visible window
        if (dir === 'down' && next > lastVisible) {
            rowVirtualizer.scrollToIndex(next, { align: 'end' });
        } else if (dir === 'up' && next < firstVisible) {
            rowVirtualizer.scrollToIndex(next, { align: 'start' });
        }

        // update selection
        flushSync(() => {
            const token = stocks[next]?.instrumentEntity?.instrumentToken;
            if (token) updateSelectedRowIndex(`11-${token}`, next);
        });
    };

    const loop = () => {
        doArrowMove();
        rafId = requestAnimationFrame(loop);
    };

    return (e) => {
        // ── don’t hijack typing ────────────────────────────────
        const tag = document.activeElement?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || document.activeElement?.isContentEditable)
            return;
        if (!rowVirtualizer || !stocks || stocks.length === 0) return;

        // ── ArrowUp / ArrowDown: one row at a time ─────────────
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            pending.current = e.key === 'ArrowDown' ? 'down' : 'up';
            if (!rafId) rafId = requestAnimationFrame(loop);
            e.preventDefault();
            return;
        }

        // ── Home: jump to first row ─────────────────────────────
        if (e.key === 'Home') {
            flushSync(() => {
                const token = stocks[0]?.instrumentEntity?.instrumentToken;
                if (token) updateSelectedRowIndex(`11-${token}`, 0);
            });
            rowVirtualizer.scrollToIndex(0, { align: 'start' });
            return;
        }

        // ── End: jump to last row ────────────────────────────────
        if (e.key === 'End') {
            const last = stocks.length - 1;
            flushSync(() => {
                const token = stocks[last]?.instrumentEntity?.instrumentToken;
                if (token) updateSelectedRowIndex(`11-${token}`, last);
            });
            rowVirtualizer.scrollToIndex(last, { align: 'end' });
            return;
        }

        // ── PageUp / PageDown / Escape need the real scroll container ──
        const scrollEl = rowVirtualizer.getScrollElement?.() || rowVirtualizer.scrollElement;
        if (!scrollEl) return;

        const scrollTop = scrollEl.scrollTop;
        const clientH   = scrollEl.clientHeight;
        const items     = rowVirtualizer.getVirtualItems();

        // filter to only rows fully in view
        const visible = items.filter(item => {
            const top    = item.start;
            const bottom = top + item.size;
            return top >= scrollTop && bottom <= scrollTop + clientH;
        });

        const firstVisible = visible[0]?.index ?? items[0]?.index;
        const pageSize     = visible.length;
        const lastIdx      = stocks.length - 1;

        // ── PageDown: one true page ─────────────────────────────
        if (e.key === 'PageDown') {
            e.preventDefault();
            const next = Math.min(firstVisible + pageSize, lastIdx);
            flushSync(() => {
                const token = stocks[next]?.instrumentEntity?.instrumentToken;
                if (token) updateSelectedRowIndex(`11-${token}`, next);
            });
            rowVirtualizer.scrollToIndex(next, { align: 'start' });
            return;
        }

        // ── PageUp: one true page ───────────────────────────────
        if (e.key === 'PageUp') {
            e.preventDefault();
            const next = Math.max(firstVisible - pageSize, 0);
            flushSync(() => {
                const token = stocks[next]?.instrumentEntity?.instrumentToken;
                if (token) updateSelectedRowIndex(`11-${token}`, next);
            });
            rowVirtualizer.scrollToIndex(next, { align: 'start' });
            return;
        }

        // ── Escape: clear current selection ───────────────────────
        if (e.key === 'Escape') {
            e.preventDefault();
            flushSync(() => {
                // newKey = null, newIndex = -1 → unselects everything
                updateSelectedRowIndex(null, -1);
            });
            return;
        }

    };
};
